/**
 * CONSAI Business App - QR Code Management Component
 */
import React, { useState, useEffect, useRef } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import { QrCode, Plus, Trash2, Star, Upload, Loader2, X, Eye } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const QRCodeManager = () => {
  const { language } = useLanguage();
  const [qrCodes, setQrCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showViewDialog, setShowViewDialog] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [newQrName, setNewQrName] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const fileInputRef = useRef(null);
  const [remainingSlots, setRemainingSlots] = useState(5);

  const fetchQrCodes = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/qr-codes`, { withCredentials: true });
      setQrCodes(res.data.qr_codes || []);
      setRemainingSlots(res.data.remaining_slots || 5 - (res.data.qr_codes?.length || 0));
    } catch (err) {
      console.error('Failed to load QR codes');
    }
    setLoading(false);
  };

  useEffect(() => { fetchQrCodes(); }, []);

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    if (file.size > 5 * 1024 * 1024) {
      toast.error('File too large. Maximum 5MB allowed');
      return;
    }
    
    setSelectedFile(file);
    const reader = new FileReader();
    reader.onload = (e) => setImagePreview(e.target.result);
    reader.readAsDataURL(file);
  };

  const handleUpload = async () => {
    if (!selectedFile || !newQrName.trim()) {
      toast.error('Please select an image and enter a name');
      return;
    }
    
    if (newQrName.length > 25) {
      toast.error('QR name must be under 25 characters');
      return;
    }
    
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', selectedFile);
      formData.append('qr_name', newQrName.trim());
      
      await axios.post(`${API_URL}/api/qr-codes/upload`, formData, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' },
        params: { qr_name: newQrName.trim() }
      });
      
      toast.success('QR code added');
      setShowAddDialog(false);
      setNewQrName('');
      setSelectedFile(null);
      setImagePreview(null);
      fetchQrCodes();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to upload QR code');
    }
    setUploading(false);
  };

  const handleSetDefault = async (qrId) => {
    try {
      await axios.put(`${API_URL}/api/qr-codes/${qrId}/set-default`, {}, { withCredentials: true });
      toast.success('Default QR code updated');
      fetchQrCodes();
    } catch (err) {
      toast.error('Failed to set default');
    }
  };

  const handleDelete = async (qrId) => {
    if (!window.confirm(language === 'hi' ? 'क्या आप इसे हटाना चाहते हैं?' : 'Delete this QR code?')) return;
    try {
      await axios.delete(`${API_URL}/api/qr-codes/${qrId}`, { withCredentials: true });
      toast.success('QR code deleted');
      fetchQrCodes();
    } catch (err) {
      toast.error('Failed to delete');
    }
  };

  const resetForm = () => {
    setNewQrName('');
    setSelectedFile(null);
    setImagePreview(null);
  };

  return (
    <Card className="bg-white">
      <CardHeader>
        <CardTitle className="flex items-center justify-between font-manrope">
          <div className="flex items-center gap-2">
            <QrCode className="h-5 w-5 text-[#021945]" />
            {language === 'hi' ? 'भुगतान QR कोड' : 'Payment QR Codes'}
          </div>
          <Badge variant="secondary" className="">
            {qrCodes.length}/5
          </Badge>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {loading ? (
          <div className="flex justify-center py-4"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>
        ) : qrCodes.length === 0 ? (
          <div className="text-center py-6 text-slate-500">
            <QrCode className="h-12 w-12 mx-auto mb-2 text-slate-300" />
            <p className="text-sm">{language === 'hi' ? 'कोई QR कोड नहीं' : 'No QR codes added yet'}</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {qrCodes.map((qr) => (
              <div key={qr.qr_id} className="relative bg-slate-50 rounded-lg p-2 group">
                <div 
                  className="aspect-square w-full bg-white rounded-lg overflow-hidden border cursor-pointer"
                  onClick={() => setShowViewDialog(qr)}
                >
                  <img src={qr.qr_image_url} alt={qr.qr_name} className="w-full h-full object-contain" />
                </div>
                <div className="mt-2">
                  <p className="text-xs font-medium truncate" title={qr.qr_name}>{qr.qr_name}</p>
                  {qr.is_default && (
                    <Badge className="bg-amber-100 text-amber-700 text-xs mt-1">
                      <Star className="h-2 w-2 mr-1" />Default
                    </Badge>
                  )}
                </div>
                <div className="absolute top-1 right-1 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  {!qr.is_default && (
                    <Button size="sm" variant="secondary" className="h-6 w-6 p-0" onClick={() => handleSetDefault(qr.qr_id)} title="Set as default">
                      <Star className="h-3 w-3" />
                    </Button>
                  )}
                  <Button size="sm" variant="destructive" className="h-6 w-6 p-0" onClick={() => handleDelete(qr.qr_id)} title="Delete">
                    <Trash2 className="h-3 w-3" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
        
        {remainingSlots > 0 && (
          <Button 
            variant="outline" 
            className="w-full" 
            onClick={() => { resetForm(); setShowAddDialog(true); }}
            data-testid="add-qr-btn"
          >
            <Plus className="h-4 w-4 mr-2" />
            {language === 'hi' ? 'QR कोड जोड़ें' : 'Add QR Code'}
          </Button>
        )}

        {/* Add QR Dialog */}
        <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="font-manrope">
                {language === 'hi' ? 'नया QR कोड जोड़ें' : 'Add New QR Code'}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <label className="text-sm text-slate-500">{language === 'hi' ? 'QR का नाम' : 'QR Name'} (max 25 chars)</label>
                <Input 
                  placeholder="PhonePe UPI, GPay, Paytm..." 
                  value={newQrName}
                  onChange={(e) => setNewQrName(e.target.value.slice(0, 25))}
                  data-testid="qr-name-input"
                />
                <p className="text-xs text-slate-400 mt-1">{newQrName.length}/25</p>
              </div>
              
              <div>
                <label className="text-sm text-slate-500">{language === 'hi' ? 'QR छवि' : 'QR Image'}</label>
                <div className="mt-2">
                  {imagePreview ? (
                    <div className="relative w-40 h-40 mx-auto bg-white rounded-lg border overflow-hidden">
                      <img src={imagePreview} alt="Preview" className="w-full h-full object-contain" />
                      <button 
                        type="button" 
                        onClick={() => { setImagePreview(null); setSelectedFile(null); }}
                        className="absolute top-1 right-1 w-6 h-6 bg-red-500 rounded-full flex items-center justify-center"
                      >
                        <X className="h-4 w-4 text-white" />
                      </button>
                    </div>
                  ) : (
                    <div 
                      className="w-40 h-40 mx-auto border-2 border-dashed border-slate-300 rounded-lg flex flex-col items-center justify-center cursor-pointer hover:border-slate-400"
                      onClick={() => fileInputRef.current?.click()}
                    >
                      <Upload className="h-8 w-8 text-slate-400 mb-2" />
                      <p className="text-xs text-slate-500">Click to upload</p>
                    </div>
                  )}
                  <input 
                    ref={fileInputRef}
                    type="file" 
                    accept="image/*" 
                    className="hidden" 
                    onChange={handleFileSelect}
                  />
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowAddDialog(false)}>
                {language === 'hi' ? 'रद्द करें' : 'Cancel'}
              </Button>
              <Button 
                onClick={handleUpload} 
                disabled={uploading || !selectedFile || !newQrName.trim()}
                className="bg-[#021945] hover:bg-[#021945]/90"
                data-testid="upload-qr-btn"
              >
                {uploading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Upload className="h-4 w-4 mr-2" />}
                {language === 'hi' ? 'अपलोड करें' : 'Upload'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* View QR Dialog */}
        <Dialog open={!!showViewDialog} onOpenChange={() => setShowViewDialog(null)}>
          <DialogContent className="sm:max-w-sm p-4">
            <DialogHeader>
              <DialogTitle className="font-manrope text-center">{showViewDialog?.qr_name}</DialogTitle>
            </DialogHeader>
            {showViewDialog && (
              <div className="flex flex-col items-center">
                <div className="w-64 h-64 bg-white rounded-lg border overflow-hidden">
                  <img src={showViewDialog.qr_image_url} alt={showViewDialog.qr_name} className="w-full h-full object-contain" />
                </div>
                <p className="text-sm text-slate-500 mt-2">
                  {language === 'hi' ? 'ग्राहक को स्कैन करने दें' : 'Let customer scan to pay'}
                </p>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </CardContent>
    </Card>
  );
};

export default QRCodeManager;
